import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { easyappParamList } from "constants/webApp";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { generatePassword } from "redux/handlers/userHandler";
import {
 getConfigurationValues,
 postEditEasyappointmentConfig
} from "redux/handlers/webAppHandler";

import { useAppDispatch } from "hooks/reduxHook";

const ChangeOwncloudConfig = ({ id }: { id: number }): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [parameter, setParameter] = useState<string>("");
 const [value, setValue] = useState<string | number>("");
 const [parameterList, setParameterList] = useState<Array<string>>([]);
 const [showPassword, setShowPassword] = useState<string>("password");

 const handleGeneratePassword = async () => {
  setValue(await generatePassword());
 };

 const handleOpen = async () => {
  const datas = await dispatch(getConfigurationValues(id, "easyappointments"));
  setParameterList(datas);
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(postEditEasyappointmentConfig(id, parameter, value));
  setLoading(false);
  handleClose();
 };

 const RenderField = (): ReactElement => {
  let findedValue = easyappParamList.find((element) => element.param === parameter);

  switch (findedValue?.type) {
   case "select":
    return (
     <FormControl>
      <InputLabel>
       <FormattedMessage id="docker.website.changeOwncloudConfigValue" />
      </InputLabel>
      <Select
       value={value}
       disabled={parameter === ""}
       label={<FormattedMessage id="docker.website.changeOwncloudConfigValue" />}
       onChange={(e) => setValue(e.target.value)}>
       {findedValue.values.map((element, index) => {
        return (
         <MenuItem key={`params-value-${index}`} value={element}>
          {element}
         </MenuItem>
        );
       })}
      </Select>
     </FormControl>
    );
   case "open_text":
    return (
     <TextField
      autoComplete="off"
      value={value}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
       e.stopPropagation();
      }}
      autoFocus
      label={intl.formatMessage({ id: "docker.website.changeOwncloudConfigValue" })}
      InputLabelProps={{ shrink: true }}
      onChange={({ currentTarget }) => setValue(currentTarget.value)}
     />
    );
   case "open_password":
    return (
     <Stack direction="row" spacing={2} alignItems="center">
      <TextField
       fullWidth={true}
       label={intl.formatMessage({ id: "docker.website.changeOwncloudConfigValue" })}
       InputLabelProps={{ shrink: true }}
       sx={{ my: 2 }}
       value={value}
       onChange={({ currentTarget }) => setValue(currentTarget.value)}
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       autoFocus
       type={showPassword}
       autoComplete="new-password"
       InputProps={{
        endAdornment: (
         <InputAdornment position="end">
          <IconButton
           aria-label="toggle password visibility"
           onClick={() => {
            setShowPassword(showPassword === "text" ? "password" : "text");
           }}
           edge="end">
           {showPassword ? (
            <IconSelector icon="VisibilityOffIcon" />
           ) : (
            <IconSelector icon="VisibilityIcon" />
           )}
          </IconButton>
         </InputAdornment>
        )
       }}
      />
      <Button onClick={handleGeneratePassword} sx={{ width: "40%" }}>
       <FormattedMessage id="app.generatePassword" />
      </Button>
     </Stack>
    );
   default:
    return <></>;
  }
 };

 return (
  <>
   <Tooltip title={<FormattedMessage id="docker.website.changeEasyappointmentConfig" />}>
    <IconButton onClick={() => handleOpen()}>
     <IconSelector icon="ServiceIcon" props={{ color: "kxOrange" }} />
    </IconButton>
   </Tooltip>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "docker.website.changeEasyappointmentConfig" })}
    handleClose={handleClose}
    handleConfirm={handleConfirm}
    disabled={loading || parameter === "" || value === "" || value === 0}>
    <Stack spacing={2}>
     <Typography variant="subtitle2">
      <FormattedMessage id="docker.website.changeOwncloudConfigMessage" />
     </Typography>
     <FormControl>
      <InputLabel>
       <FormattedMessage id="docker.website.changeOwncloudConfigParameter" />
      </InputLabel>
      <Select
       value={parameter}
       label={<FormattedMessage id="docker.website.changeOwncloudConfigParameter" />}
       onChange={(e) => {
        setValue("");
        setParameter(e.target.value);
       }}>
       {parameterList.map((element, index) => {
        return (
         <MenuItem key={`parameter-${index}`} value={element}>
          {element}
         </MenuItem>
        );
       })}
      </Select>
     </FormControl>
     <RenderField />
    </Stack>
   </AppModal>
  </>
 );
};

export default ChangeOwncloudConfig;
